import React, { useState, useEffect } from 'react';
import moncashImg from '../components/moncash.jpg';
import monetahImg from '../components/monetah.jpg';
import { useAuthStore } from '../stores/authStore';
import api from '../services/api';
import Coin from '../components/Coin';

const HTG_TO_COINS = 10; // 1 HTG = 10 coins
const COINS_TO_HTG = 0.1; // 10 coins = 1 HTG

const convertDepositToCoins = (htg) => htg * HTG_TO_COINS;
const convertWithdrawToHTG = (coins) => coins * COINS_TO_HTG;

const Payments = () => {
  const { user, updateBalance } = useAuthStore();
  const [depositAmount, setDepositAmount] = useState(''); // en HTG
  const [withdrawAmountCoins, setWithdrawAmountCoins] = useState(''); // en coins
  const [paymentMethod, setPaymentMethod] = useState('moncash');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [transactions, setTransactions] = useState([]);
  const [activeTab, setActiveTab] = useState('history'); // 'deposit', 'withdraw' ou 'history'

  useEffect(() => {
    if (user) {
      fetchTransactions();
    }
  }, [user]);

  const fetchTransactions = async () => {
    try {
      const response = await api.get('/payments/history');
      // Accepte un tableau direct ou un objet avec clé 'transactions'
      const data = response.data;
      if (Array.isArray(data)) {
        setTransactions(data);
      } else if (Array.isArray(data.transactions)) {
        setTransactions(data.transactions);
      } else {
        setTransactions([]);
      }
    } catch (error) {
      console.error('Erreur lors du chargement des transactions:', error);
      setTransactions([]);
    }
  };

  const handleDeposit = async (e) => {
    e.preventDefault();
    if (!depositAmount || parseFloat(depositAmount) <= 0) {
      setMessage('Veuillez entrer un montant valide');
      return;
    }

    setLoading(true);
    setMessage('');

    try {
      const response = await api.post('/payments/deposit', {
        amount: parseFloat(depositAmount), // en HTG
        method: paymentMethod
      });

      if (response.data.redirectUrl) {
        setMessage('Paiement initié avec succès ! Redirection vers la page de paiement...');
        // Rediriger vers la page de paiement MonCash
        window.location.href = response.data.redirectUrl;
      }
    } catch (error) {
      setMessage('Erreur lors du traitement du paiement: ' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      'pending': { text: 'En attente', color: 'bg-yellow-100 text-yellow-800' },
      'completed': { text: 'Complété', color: 'bg-green-100 text-green-800' },
      'failed': { text: 'Échoué', color: 'bg-red-100 text-red-800' },
      'cancelled': { text: 'Annulé', color: 'bg-gray-100 text-gray-800' }
    };
    
    const config = statusConfig[status] || statusConfig['pending'];
    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full ${config.color}`}>
        {config.text}
      </span>
    );
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600">Veuillez vous connecter pour accéder aux paiements</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Paiements</h1>
          <p className="text-gray-600">Rechargez votre compte ou effectuez un retrait</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Colonne gauche : boutons Dépôt / Retrait */}
          <div className="bg-white rounded-lg shadow-md p-6 flex flex-col items-center gap-6">
            <div className="w-full bg-orange-50 rounded-lg p-4 text-center mb-4">
              <div className="text-gray-700 mb-1">Solde actuel :</div>
              <div className="text-2xl font-bold text-orange-600"><Coin amount={parseFloat(user.balance || 0).toFixed(2)} symbol="HTG" /></div>
            </div>
            <button
              className={`w-full py-4 text-xl font-bold rounded-lg flex items-center justify-center gap-2 transition-colors ${activeTab === 'deposit' ? 'bg-orange-500 text-white' : 'bg-gray-100 text-gray-800 hover:bg-orange-100'}`}
              onClick={() => setActiveTab('deposit')}
            >
              <span>💰</span> Dépôt
            </button>
            <button
              className={`w-full py-4 text-xl font-bold rounded-lg flex items-center justify-center gap-2 transition-colors ${activeTab === 'withdraw' ? 'bg-orange-500 text-white' : 'bg-gray-100 text-gray-800 hover:bg-orange-100'}`}
              onClick={() => setActiveTab('withdraw')}
            >
              <span>💸</span> Retrait
            </button>
            <button
              className={`w-full py-2 text-base font-medium rounded-lg flex items-center justify-center gap-2 mt-2 transition-colors ${activeTab === 'history' ? 'bg-orange-100 text-orange-700' : 'bg-gray-100 text-gray-800 hover:bg-orange-100'}`}
              onClick={() => setActiveTab('history')}
            >
              <span>📜</span> Historique
            </button>
          </div>

          {/* Colonne droite : contenu dynamique */}
          <div className="lg:col-span-2 bg-white rounded-lg shadow-md p-6">
            {activeTab === 'deposit' && (
              <>
                <h2 className="text-xl font-semibold text-gray-900 mb-6">Déposer des fonds</h2>
                {message && (
                  <div className={`mb-4 p-3 rounded-lg ${
                    message.includes('succès') 
                      ? 'bg-green-100 text-green-700' 
                      : 'bg-red-100 text-red-700'
                  }`}>
                    {message}
                  </div>
                )}
                <form onSubmit={handleDeposit} className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Montant à déposer
                    </label>
                    <input
                      type="number"
                      value={depositAmount}
                      onChange={(e) => setDepositAmount(e.target.value)}
                      placeholder="Entrez le montant"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                      min="1"
                      required
                    />
                    {depositAmount && parseFloat(depositAmount) > 0 && (
                      <div className="text-sm text-gray-600 mt-1">
                        Vous recevrez: <span className="font-semibold text-orange-700"><Coin amount={convertDepositToCoins(parseFloat(depositAmount)).toFixed(0)} /></span>
                      </div>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Méthode de paiement
                    </label>
                    <div className="space-y-3">
                      <label className="flex items-center">
                        <input
                          type="radio"
                          name="paymentMethod"
                          value="moncash"
                          checked={paymentMethod === 'moncash'}
                          onChange={(e) => setPaymentMethod(e.target.value)}
                          className="mr-3"
                        />
                        <div className="flex items-center">
                          <img src={moncashImg} alt="MonCash" className="h-6 w-auto mr-2 rounded" />
                          <span>MonCash</span>
                        </div>
                      </label>
                      <label className="flex items-center">
                        <input
                          type="radio"
                          name="paymentMethod"
                          value="monetah"
                          checked={paymentMethod === 'monetah'}
                          onChange={(e) => setPaymentMethod(e.target.value)}
                          className="mr-3"
                        />
                        <div className="flex items-center">
                          <img src={monetahImg} alt="Monetah" className="h-6 w-auto mr-2 rounded" />
                          <span>Monetah</span>
                        </div>
                      </label>
                    </div>
                  </div>
                  <button
                    type="submit"
                    disabled={loading || !depositAmount}
                    className="w-full bg-orange-500 text-white py-3 px-4 rounded-lg hover:bg-orange-600 disabled:bg-gray-300 disabled:cursor-not-allowed"
                  >
                    {loading ? 'Traitement...' : 'Procéder au dépôt'}
                  </button>
                </form>
                <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                  <h3 className="font-medium text-gray-900 mb-2">Informations importantes :</h3>
                  <ul className="text-sm text-gray-600 space-y-1">
                    <li>• Les dépôts sont traités en temps réel</li>
                    <li>• Votre compte sera crédité immédiatement après confirmation</li>
                    <li>• Les montants minimum et maximum sont respectés</li>
                    <li>• En cas de problème, contactez le support</li>
                  </ul>
                </div>
              </>
            )}
            {activeTab === 'withdraw' && (
              <>
                <h2 className="text-xl font-semibold text-gray-900 mb-6">Retirer des fonds</h2>
                <div className="mb-4 p-3 rounded-lg bg-blue-50 text-blue-700">
                  Les retraits sont traités manuellement par l’équipe. Veuillez indiquer le montant et la méthode souhaitée, puis valider votre demande.
                </div>
                <form className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Montant à retirer (coins)
                    </label>
                    <input
                      type="number"
                      value={withdrawAmountCoins}
                      onChange={(e) => setWithdrawAmountCoins(e.target.value)}
                      placeholder="Entrez le montant"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                      min="1"
                      required
                    />
                    {withdrawAmountCoins && parseFloat(withdrawAmountCoins) > 0 && (
                      <div className="text-sm text-gray-600 mt-1">
                        Vous recevrez: <span className="font-semibold text-green-700">{convertWithdrawToHTG(parseFloat(withdrawAmountCoins)).toFixed(2)} HTG</span>
                      </div>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Méthode de retrait
                    </label>
                    <div className="space-y-3">
                      <label className="flex items-center">
                        <input
                          type="radio"
                          name="paymentMethod"
                          value="moncash"
                          checked={paymentMethod === 'moncash'}
                          onChange={(e) => setPaymentMethod(e.target.value)}
                          className="mr-3"
                        />
                        <div className="flex items-center">
                          <img src={moncashImg} alt="MonCash" className="h-6 w-auto mr-2 rounded" />
                          <span>MonCash</span>
                        </div>
                      </label>
                      <label className="flex items-center">
                        <input
                          type="radio"
                          name="paymentMethod"
                          value="monetah"
                          checked={paymentMethod === 'monetah'}
                          onChange={(e) => setPaymentMethod(e.target.value)}
                          className="mr-3"
                        />
                        <div className="flex items-center">
                          <img src={monetahImg} alt="Monetah" className="h-6 w-auto mr-2 rounded" />
                          <span>Monetah</span>
                        </div>
                      </label>
                    </div>
                  </div>
                  <button
                    type="button"
                    disabled={loading || !withdrawAmountCoins}
                    className="w-full bg-orange-500 text-white py-3 px-4 rounded-lg hover:bg-orange-600 disabled:bg-gray-300 disabled:cursor-not-allowed"
                    onClick={() => alert('La demande de retrait sera bientôt disponible.')}
                  >
                    Demander un retrait
                  </button>
                </form>
                <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                  <h3 className="font-medium text-gray-900 mb-2">Informations importantes :</h3>
                  <ul className="text-sm text-gray-600 space-y-1">
                    <li>• Les retraits sont validés manuellement sous 24h</li>
                    <li>• Assurez-vous que vos informations sont à jour</li>
                    <li>• En cas de problème, contactez le support</li>
                  </ul>
                </div>
              </>
            )}
            {activeTab === 'history' && (
              <div>
                <h2 className="text-xl font-semibold text-gray-900 mb-6">Historique des transactions</h2>
                {transactions.length === 0 ? (
                  <div className="text-center py-8">
                    <div className="text-gray-400 text-4xl mb-4">💳</div>
                    <p className="text-gray-600">Aucune transaction trouvée</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {transactions.map((transaction) => (
                      <div key={transaction.id} className="border border-gray-200 rounded-lg p-4">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center">
                            <span className="text-2xl mr-3">
                              {transaction.type === 'deposit' ? '💰' : '💸'}
                            </span>
                            <div>
                              <h3 className="font-medium text-gray-900">
                                {transaction.type === 'deposit' ? 'Recharge' : 'Retrait'}
                              </h3>
                              <p className="text-sm text-gray-500">
                                {formatDate(transaction.createdAt)}
                              </p>
                            </div>
                          </div>
                          <div className="text-right">
                            <div className="font-semibold text-gray-900">
                              {transaction.type === 'deposit' ? '+' : '-'}<Coin amount={transaction.amount} symbol="HTG" />
                            </div>
                            {getStatusBadge(transaction.status)}
                          </div>
                        </div>
                        {transaction.reference && (
                          <div className="text-sm text-gray-600">
                            Référence: {transaction.reference}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Payments; 